import { useEffect, useState, useCallback } from 'react';
import { DTableProps } from 'components/darwin-ui';

type SortDirection = 'ASC' | 'DESC';

export interface SortOrder {
  sortBy: string;
  order: SortDirection;
}

type OnChangeHandler = NonNullable<DTableProps<any>['onChange']>;

interface TableControlsOptions {
  initialPageSize?: number;
  initialPage?: number;
  defaultSortBy?: string;
  defaultSortOrder?: SortDirection;
  fieldMapping?: Record<string, string>;
}

const useTableControls = ({
  initialPageSize = 10,
  initialPage = 1,
  defaultSortBy = 'id',
  defaultSortOrder = 'ASC',
  fieldMapping = {},
}: TableControlsOptions = {}) => {
  const [pagination, setPagination] = useState({
    pageSize: initialPageSize,
    currentPage: initialPage,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>({
    sortBy: defaultSortBy,
    order: defaultSortOrder,
  });

  const { pageSize, currentPage } = pagination;

  const onPageSizeChange = useCallback((current: number, size: number) => {
    setPagination({ pageSize: size, currentPage: current });
  }, []);

  const setCurrentPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, setCurrentPage]);

  const onTableChange = useCallback<OnChangeHandler>(
    (paginationConfig, _filters, sorter) => {
      const { current = currentPage, pageSize: size = pageSize } =
        paginationConfig ?? {};
      setPagination({ currentPage: current, pageSize: size });

      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const { field, order } = currentSorter ?? {};

      if (!field) return;

      if (!order) {
        setSortOrder({
          sortBy: defaultSortBy,
          order: defaultSortOrder,
        });
      } else {
        const frontendField = Array.isArray(field)
          ? field.join('.')
          : (field as string | undefined) ?? '';

        // Map frontend field to backend field if mapping exists
        const backendField = fieldMapping[frontendField] || frontendField;

        const sortOrder: SortDirection = order === 'descend' ? 'DESC' : 'ASC';
        setSortOrder({ sortBy: backendField, order: sortOrder });
      }
    },
    [currentPage, defaultSortBy, defaultSortOrder, pageSize, fieldMapping]
  );

  return {
    pageSize,
    currentPage,
    searchQuery,
    sortOrder,
    onPageSizeChange,
    setCurrentPage,
    setSearchQuery,
    onTableChange,
  };
};

export default useTableControls;
